// The makers' own documents for each lock and operator we sell: the warranty terms that apply
// to it, and the manual / fitting template / brochure for that exact model. Every link points at
// the maker's own site (or its NZ distributor's), never a manuals mirror, so a customer reads the
// current edition. Researched 17 Sep 2026; `scripts/check-product-docs.py` re-fetches every link
// and confirms the document really mentions the model, `src/content/productDocs.test.js` proves
// each document is attached to the right product page. CommonJS, no spread/destructuring (CRA
// content gotcha).
//
// A document is { label, href, models, scan? }:
//   models — the tokens a reader would expect inside the document (model numbers, series or
//            brand); the offline test matches them against the product, the network check
//            against the document text.
//   scan   — the PDF is a scan or drawing with no extractable text; the network check then
//            only confirms it is a real PDF and that the URL names the model.
// A product entry may add `aliases`: other names its documents are filed under (the Trimec
// brand behind the Lockwood ES9000, the YRD256 part number behind the Yale Assure SL).

function doc(label, href, models, scan) {
  return { label: label, href: href, models: models, scan: !!scan };
}

// ── Shared warranty documents ──────────────────────────────────────────────────────────
const YALE_WARRANTY = doc('Yale New Zealand warranty terms — 10 years mechanical, 3 years electronic (ASSA ABLOY NZ)', 'https://www.yalehome.com/nz/en/documents/privacy-center/Yale%20Warranty.pdf', ['Yale', 'YALE']);
const SCHLAGE_WARRANTY = doc('Schlage residential warranty periods, New Zealand (Allegion, December 2025)', 'https://www.schlage.co.nz/content/dam/schlage-nz/warranty/2512%20Door%20hardware%20warranties%20-%20Residential.pdf', ['Schlage', 'Artus', 'Resolute', 'Ascent', 'Encode']);
const SCHLAGE_CARE = doc('Schlage smart lock care and maintenance guide (Allegion)', 'https://www.schlage.co.nz/content/dam/schlage-nz/warranty/2310%20Schlage%20Smart%20Lock%20Maintenance%20Guide.pdf', ['Schlage']);
const ALLEGION_COMMERCIAL_WARRANTY = doc('Allegion New Zealand door hardware warranty — commercial products (FSH)', 'https://www.allegion.co.nz/content/dam/allegion-co-nz/resource-hub/downloads/2605%20Allegion%20NZ%20Door%20Hardware%20Commercial%20Warranty1.pdf.coredownload.pdf', ['Allegion', 'FSH']);
const LOCKWOOD_WARRANTY = doc('Lockwood warranty terms — 3 years electronic, 25 years mechanical (ASSA ABLOY)', 'https://www.assaabloy.com/au/en/documents/warranty/Lockwood%20Warranty.pdf', ['Lockwood', 'LOCKWOOD']);
const TRIMEC_WARRANTY = doc('Trimec Tri-Care warranty — 5 years (ASSA ABLOY)', 'https://www.assaabloy.com/au/en/documents/warranty/TRI-CARE%20Warranty.pdf', ['TRIMEC', 'Trimec']);
const AQARA_WARRANTY = doc('Aqara limited warranty, Australia and New Zealand — 12 months (Konec)', 'https://www.aqarahome.nz/Support/warranty.html', ['Aqara']);
const AQARA_CGA = doc('Aqara consumer guarantees statement for New Zealand (Konec)', 'https://www.aqarahome.nz/Support/anzwarranty.html', ['Aqara']);
const ELOK_TEMPLATES = doc('E-LOK cropping templates (all models)', 'https://www.e-lok.com/e-lok-cropping-templates', ['E-LOK']);
const AHS_WARRANTY = doc('AHS A-Series operator warranty — 12 months (Architectural Hardware Supplies)', 'https://ahs.co.nz/wp-content/uploads/2020/08/ahs_ASeries_GenericWarranty2024.pdf', ['A-Series', 'ASeries'], true);
const AHS_MANUAL = doc('A-Series A200 SW installation manual with certificate of conformity', 'https://ahs.co.nz/wp-content/uploads/2020/08/A-200SW-Manual-with-COC.pdf', ['A-200SW', 'SW 200', 'A200']);
const AHS_BROCHURE = doc('AHS A-Series swing door operator brochure', 'https://ahs.co.nz/wp-content/uploads/2021/03/AHS-A-Series-Swing-Door-Operator.pdf', ['A-Series'], true);

// ── E-LOK (Elements Hardware NZ). Each series flyer carries the warranty line, "2 Year
//    Mechanical & Electrical", so it is the warranty document as well as the brochure. ──
const ELOK_9_MANUAL = doc('E-LOK 905 / 915 / 905W / 915W user manual', 'https://www.e-lok.com/_files/ugd/c6c055_4b60e528c3f64b3bbfc0f70627d076ba.pdf', ['905', '915', '905W', '915W']);
const ELOK_9_DIMS = doc('E-LOK 9-Series dimensions', 'https://www.e-lok.com/_files/ugd/c6c055_213ff65be24544458fe57ca2e715c869.pdf', ['9-Series']);
const ELOK_7_MANUAL = doc('E-LOK 705 / 707 / 715 / 717 user manual', 'https://www.e-lok.com/_files/ugd/c6c055_df3dcb38d0e24fb5902892b3138d8b4e.pdf', ['705', '707', '715', '717']);
const ELOK_7_DIMS = doc('E-LOK 7-Series body dimensions (705)', 'https://www.e-lok.com/_files/ugd/c6c055_218c91ee0231466d83705a630557347e.pdf', ['705', '7-Series'], true);
const ELOK_8_MANUAL = doc('E-LOK 805 / 805W user manual', 'https://www.e-lok.com/_files/ugd/c6c055_a19a153f005b4576a6a3f99cf337babc.pdf', ['805', '805W']);
const ELOK_8_DIMS = doc('E-LOK 8-Series body dimensions (805)', 'https://www.e-lok.com/_files/ugd/c6c055_da32a20b3b8b41b993454f26e8759286.pdf', ['805', '8-Series'], true);

const productDocs = {
  'e-lok-905': {
    warranty: [doc('E-LOK 9-Series flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_c3291ca0af0c49788667c6093aeeb346.pdf', ['905', '905W', '9-Series'])],
    docs: [ELOK_9_MANUAL, ELOK_9_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-905w': {
    warranty: [doc('E-LOK 9-Series flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_c3291ca0af0c49788667c6093aeeb346.pdf', ['905', '905W', '9-Series'])],
    docs: [ELOK_9_MANUAL, ELOK_9_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-915-sliding': {
    warranty: [doc('E-LOK 915 sliding flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_0e1ff7134d154a2893e56d6277659e71.pdf', ['915', '9-Series'])],
    docs: [ELOK_9_MANUAL, ELOK_9_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-715-sliding': {
    warranty: [doc('E-LOK 715 / 717 sliding flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_e3a2f23de39042e1a4567da898018cb8.pdf', ['715', '717', '7-Series'])],
    docs: [ELOK_7_MANUAL, ELOK_7_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-717-sliding': {
    warranty: [doc('E-LOK 715 / 717 sliding flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_e3a2f23de39042e1a4567da898018cb8.pdf', ['715', '717', '7-Series'])],
    docs: [ELOK_7_MANUAL, ELOK_7_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-705': {
    warranty: [doc('E-LOK 705 / 707 flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_d8f78b62bf034a53af175b71c374a733.pdf', ['705', '707', '7-Series'])],
    docs: [ELOK_7_MANUAL, ELOK_7_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-707': {
    warranty: [doc('E-LOK 705 / 707 flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_d8f78b62bf034a53af175b71c374a733.pdf', ['705', '707', '7-Series'])],
    docs: [ELOK_7_MANUAL, ELOK_7_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-805w': {
    warranty: [doc('E-LOK 805 / 805W flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_f265feb3320a4cdebcb9617fd62995d1.pdf', ['805', '805W', '8-Series'])],
    docs: [ELOK_8_MANUAL, ELOK_8_DIMS, ELOK_TEMPLATES],
  },
  'e-lok-1005': {
    warranty: [doc('E-LOK 1005 flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_6d2ac897d5f34375a2962b30280985c2.pdf', ['1005', '10-Series'])],
    docs: [
      doc('E-LOK 1005 user manual', 'https://www.e-lok.com/_files/ugd/c6c055_d8fc209b90204e6eac0d8c2e4e1498e3.pdf', ['1005']),
      doc('E-LOK 10-Series body dimensions', 'https://www.e-lok.com/_files/ugd/c6c055_36f8cb68a79f48e9b88b39ff5c1b668d.pdf', ['1005', '10-Series']),
      ELOK_TEMPLATES,
    ],
  },
  'e-lok-605': {
    warranty: [doc('E-LOK 605 flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_aa54db87656c459baf899def67491ced.pdf', ['605', '6-Series'])],
    docs: [
      doc('E-LOK 6-Series user manual', 'https://www.e-lok.com/_files/ugd/c6c055_c75a881b980946528a120887b37dd8df.pdf', ['605', '6-Series']),
      doc('E-LOK 6-Series dimensions', 'https://www.e-lok.com/_files/ugd/c6c055_1ed1183220714775a03c1d686f72a3a2.pdf', ['605', '6-Series'], true),
      ELOK_TEMPLATES,
    ],
  },
  'e-lok-505': {
    warranty: [doc('E-LOK 505 flyer — residential warranty 2 years mechanical and electrical', 'https://www.e-lok.com/_files/ugd/c6c055_a16d7d459a0f4334b3dc08d9162a1fdd.pdf', ['505', '5-Series'])],
    docs: [
      doc('E-LOK 5-Series user manual', 'https://www.e-lok.com/_files/ugd/c6c055_3e05f8a0182149d49989ca28b30bda83.pdf', ['505', '5-Series']),
      doc('E-LOK 5-Series dimensions', 'https://www.e-lok.com/_files/ugd/c6c055_82122b878bcb451593e20b574c9ebde8.pdf', ['505', '5-Series'], true),
      ELOK_TEMPLATES,
    ],
  },

  // ── Yale (ASSA ABLOY NZ; manuals on yalehome.com/nz and Yale's own NZ web shop) ──
  'yale-unity-slim': {
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale Unity Slim smart lock user guide', 'https://www.yalehome.com/nz/en/product-assets/smart-digital-locks/smart-locks/unity-slim/documents/yale-unity-slim-smart-lock-user-guide.pdf', ['Unity', 'Slim']),
      doc('Yale Unity Slim fitting instructions (A3)', 'https://www.yalehome.com/nz/en/product-assets/smart-digital-locks/smart-locks/unity-slim/documents/yale-unity-slim-smart-lock-fitting-instructions-a3.pdf', ['Unity', 'Slim']),
      doc('Yale Unity Slim routing template', 'https://www.shopyalehome.co.nz/media/UserGuide/YaleUnitySlim_Routing_Template.pdf', ['Unity', 'Slim'], true),
      doc('Yale Unity Slim flyer', 'https://www.shopyalehome.co.nz/media/UserGuide/YaleUnitySlim_Flyer.pdf', ['Unity', 'Slim']),
    ],
  },
  'yale-unity-entrance': {
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale Unity Entrance lock installation guide', 'https://www.yalehome.com/nz/en/product-assets/keyless-entry/smart-locks/unity-entrance-series/yale-unity-entrance-lock/assets/documents/yale-unity-entrance-lock-installation-guide.pdf', ['Unity', 'Entrance']),
      doc('Yale Unity Entrance lock fitting instructions and template', 'https://www.yalehome.com/nz/en/product-assets/keyless-entry/smart-locks/unity-entrance-series/yale-unity-entrance-lock/assets/documents/yale-unity-entrance-lock-fitting-instructions.pdf', ['Unity', 'Entrance']),
      doc('Yale Unity Entrance lock flyer', 'https://www.shopyalehome.co.nz/media/UserGuide/YUR_DEL_1_SIL_YA_EN_TS1.pdf', ['Unity', 'Entrance']),
    ],
  },
  'yale-ydm3109a': {
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale YDM3109A user guide', 'https://www.yalehome.com/nz/en/product-assets/smart-digital-locks/smart-locks/ydm3109a/documents/yale-3109a-user-guide.pdf', ['3109A', '3109']),
      doc('Yale YDM3109A fitting instructions (A3)', 'https://www.shopyalehome.co.nz/media/UserGuide/3019FITTINGA3_YA_EN_FIT1_V1_3109.pdf', ['3109']),
      doc('Yale YDM3109A brochure', 'https://www.yalehome.com/nz/en/product-assets/smart-digital-locks/smart-locks/ydm3109a/documents/yale-3109a-brochure.pdf', ['3109A', '3109']),
    ],
  },
  'yale-ydm7220': {
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale YDM7220 user guide', 'https://www.shopyalehome.co.nz/media/UserGuide/YDM7220_YA_EN_II1.pdf', ['7220']),
      doc('Yale YDM7220 installation instructions', 'https://www.shopyalehome.co.nz/media/UserGuide/YDM7220MBK%2360%23ANZ_YA_EN_II2.pdf', ['7220']),
      doc('Yale YDM7220 tail bar preparation', 'https://www.yalehome.com/nz/en/product-assets/smart-digital-locks/smart-locks/ydm7220/documents/how-to-cut-yale-7220-tail-bar.pdf', ['7220']),
      doc('Yale YDM7220 flyer', 'https://www.shopyalehome.co.nz/media/UserGuide/7220-FLY_YA_EN_TS1.pdf', ['7220']),
    ],
  },
  'yale-assure-sl': {
    aliases: ['YRD256'],
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale Assure Lock SL installation and programming instructions', 'https://www.shopyalehome.co.nz/media/UserGuide/YRL_SLKFTSINSTL_Yale_Assure_Lock_SL_Programming_Instructions.pdf', ['Assure']),
      doc('Yale Assure Lock SL installation view', 'https://www.shopyalehome.co.nz/media/UserGuide/Assure_SL_Installation_View.pdf', ['Assure', 'YRD256'], true),
      doc('Yale Assure Lock SL door template', 'https://www.shopyalehome.co.nz/media/UserGuide/YRD256_Assure_SL_door_Template.pdf', ['Assure', 'YRD256'], true),
      doc('Yale Assure Lock SL flyer', 'https://www.shopyalehome.co.nz/media/UserGuide/YRD256NRSN_YA_EN_TS1.pdf', ['Assure']),
    ],
  },
  'yale-assure-keyed': {
    aliases: ['YRD226', 'Deadbolt'],
    warranty: [YALE_WARRANTY],
    docs: [
      doc('Yale Assure Lock (keyed, YRD226) installation and programming instructions', 'https://www.shopyalehome.co.nz/media/UserGuide/Yale_YRD226_Installation_and_Programming_Instructions.pdf', ['Assure', 'YRD226']),
      doc('Yale Assure deadbolt marking template', 'https://www.shopyalehome.co.nz/media/UserGuide/Yale_Deadbolt_Marking_Template.pdf', ['Assure', 'Deadbolt'], true),
      doc('Yale Assure Lock flyer', 'https://www.shopyalehome.co.nz/media/UserGuide/ASSUREDDLFLY_YA_EN_TS1.pdf', ['Assure']),
    ],
  },

  // ── Schlage (Allegion NZ; the NZ site serves its documents from Salsify) ──
  'schlage-artus': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Artus quick start guide', 'https://images.salsify.com/image/upload/s--mxJZtFiS--/d4woy8gmxr1yqc1htd4n.pdf', ['Artus']),
      doc('Schlage Artus door preparation template (print A3, 1:1)', 'https://images.salsify.com/image/upload/s--H5_YwJGY--/vhf5s4lybwlqj7mwnnvh.pdf', ['ARTUS', 'Artus']),
      doc('Schlage Artus brochure', 'https://images.salsify.com/image/upload/s--MNlGzDIW--/bmlthogtxs9dkcfv0hog.pdf', ['Artus']),
      doc('Schlage Artus cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--yU7b4-VV--/gxyrioxprnueeqoqyppl.pdf', ['Artus']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-artus-sliding': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Artus sliding door lock quick start guide', 'https://images.salsify.com/image/upload/s--6vMMSryY--/evojfsaxrmho7opacelt.pdf', ['Artus']),
      doc('Schlage Artus sliding door preparation template', 'https://images.salsify.com/image/upload/s--sqJHkyPG--/ni9wnhlp5lcbqp854dcq.pdf', ['ARTUS', 'Artus']),
      doc('Schlage Artus brochure', 'https://images.salsify.com/image/upload/s--g32NEGZ_--/qwghqwosvawpdgsufpti.pdf', ['Artus']),
      doc('Schlage Artus sliding door lock cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--8l_h4ZU9--/h4hqrhprcucwawo0nnko.pdf', ['Artus']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-resolute': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Resolute quick start guide', 'https://images.salsify.com/image/upload/s--5NqjuFHf--/b65alsgzwf4wp5pnkzej.pdf', ['Resolute']),
      doc('Schlage Resolute door preparation template (print A3, 1:1)', 'https://images.salsify.com/image/upload/s--UgE8Cwft--/d1hyzdflmmq2qaefhsqt.pdf', ['Resolute', 'RESOLUTE']),
      doc('Schlage Resolute brochure', 'https://images.salsify.com/image/upload/s--zT3USRdx--/bxoqybpjornhdbdz5qmq.pdf', ['Resolute']),
      doc('Schlage Resolute cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--4VVLj43G--/hyjzw4yw3xcanwtlb46l.pdf', ['Resolute']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-omnia': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Omnia installation instructions', 'https://images.salsify.com/image/upload/s--nUcPU3TH--/igkjxojb22neikuee45u.pdf', ['Omnia']),
      doc('Schlage Omnia brochure', 'https://images.salsify.com/image/upload/s--MX5QQ7Y7--/cwnxqh24xr1xecemswgp.pdf', ['Omnia']),
      doc('Schlage Omnia cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--yyt3Tuof--/pcvx1kgxfr2yz134ey3s.pdf', ['Omnia']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-ascent-deadbolt': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Ascent smart deadbolt quick start guide', 'https://images.salsify.com/image/upload/s---bavEvlc--/podurqubxzme2ikjmqgr.pdf', ['Ascent']),
      doc('Schlage Ascent deadbolt door preparation template (print 1:1)', 'https://images.salsify.com/image/upload/s--zjeC0g7w--/zmulhdk86dvjzvslasoj.pdf', ['Ascent']),
      doc('Schlage Ascent brochure', 'https://images.salsify.com/image/upload/s--a1KeoqQK--/fsnxl45lrbr1qzau0vi1.pdf', ['Ascent']),
      doc('Schlage Ascent cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--ttP3rcrB--/wzaihop6jjgxhecy2vh3.pdf', ['Ascent']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-ascent-entrance': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Ascent smart entrance lever quick start guide', 'https://images.salsify.com/image/upload/s--_O3HK3QV--/bhn1852szj9jdbba6b7s.pdf', ['Ascent']),
      doc('Schlage Ascent entrance lever door preparation template (print 1:1)', 'https://images.salsify.com/image/upload/s--_zyxCq30--/ga2yzimmdhjejcmhkngq.pdf', ['Ascent']),
      doc('Schlage Ascent brochure', 'https://images.salsify.com/image/upload/s--a1KeoqQK--/fsnxl45lrbr1qzau0vi1.pdf', ['Ascent']),
      doc('Schlage Ascent cut sheet (specifications)', 'https://images.salsify.com/image/upload/s--ttP3rcrB--/wzaihop6jjgxhecy2vh3.pdf', ['Ascent']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-encode': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [
      doc('Schlage Encode quick start guide', 'https://images.salsify.com/image/upload/s--aUB3Iv0C--/w5bvx1hyywpmhajemex0.pdf', ['Encode']),
      doc('Schlage Encode door preparation — door with no holes', 'https://images.salsify.com/image/upload/s--S0L1aT9L--/r8vh7hre5yous4bxijus.pdf', ['Encode']),
      doc('Schlage Encode — adding and deleting user codes on the touchscreen', 'https://images.salsify.com/image/upload/s--xf1IoWFN--/nhhbasp68oqrcnt6vm4n.pdf', ['Encode']),
      doc('Schlage Encode brochure and specifications', 'https://images.salsify.com/image/upload/s--v6ONPFBl--/hexd32c8ylvwbpt30mng.pdf', ['Encode']),
      SCHLAGE_CARE,
    ],
  },
  'schlage-s480-rim-lock': {
    warranty: [SCHLAGE_WARRANTY],
    docs: [SCHLAGE_CARE],
    note: 'Schlage does not publish the S-480 manual online; the printed user guide comes in the box and we hand it over at install.',
  },

  // ── Lockwood, Trimec and FSH (ASSA ABLOY NZ and Allegion NZ) ──
  'lockwood-cortex': {
    aliases: ['CTX530', 'CTX 530'],
    warranty: [LOCKWOOD_WARRANTY],
    docs: [
      doc('Lockwood Cortex brochure (ASSA ABLOY NZ)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/CORTEXBRONZ.pdf', ['Cortex']),
      doc('Lockwood Cortex programming guide', 'https://www.lockweb.com.au/au/en/documents/technical-downloads/electromechanical-solutions/commercial-digital-door-locks/cortex-digital-door-lock/Cortex%20Programming%20Guide.pdf', ['Cortex'], true),
      doc('Lockwood Cortex CTX 530 door template', 'https://www.lockweb.com.au/au/en/documents/technical-downloads/electromechanical-solutions/commercial-digital-door-locks/cortex-digital-door-lock/CTX%20530%20Template.pdf', ['CTX 530', 'CTX530'], true),
      doc('Lockwood electronic access control catalogue', 'https://www.lockweb.com.au/au/en/documents/catalogues/electronic-access-control/Lockwood%20Electronic%20Access%20Control%20Catalogue.pdf', ['Cortex', 'CORTEX']),
    ],
  },
  'lockwood-es9000-electric-strike': {
    aliases: ['Trimec', 'ES9100'],
    warranty: [TRIMEC_WARRANTY],
    docs: [
      doc('ES9000 electric strike installation instructions (ASSA ABLOY NZ)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/ES9000_Installation_Instructions.pdf', ['ES9000']),
      doc('Trimec electric strikes catalogue section (ASSA ABLOY NZ)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/Trimec%20Electric%20Strikes%20Catalogue%20Section.pdf', ['ES9000']),
      doc('Lockwood ES9000 / ES9100 installation instructions', 'https://www.lockweb.com.au/au/en/documents/technical-downloads/electromechanical-solutions/electric-strikes/es9000-and-es9100/ES9100-ES900%20-%20Installation%20Instructions.pdf', ['ES9000', 'ES9100'], true),
    ],
  },
  'lockwood-maglock': {
    aliases: ['Trimec', 'Allegion', 'EML', 'Z4 / Z8', 'Z4 Electromagnetic Lock', 'Z4 and Z8'],
    warranty: [TRIMEC_WARRANTY, ALLEGION_COMMERCIAL_WARRANTY],
    docs: [
      doc('Trimec Z4 / Z8 single monitored electromagnetic lock installation (ASSA ABLOY NZ)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/Z8_Z4_Z2_Single%20Mtd_Inst.pdf', ['Z4', 'EML']),
      doc('Lockwood electromechanical door solutions catalogue (Z4 / Z8 electromagnetic locks)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/Electromechanical%20Door%20Solutions.pdf', ['Z4 Electromagnetic Lock', 'Z4 and Z8']),
      doc('FSH FEM4300 series product catalogue (Allegion NZ)', 'https://www.allegion.co.nz/content/dam/allegion-co-nz/resource-hub/downloads/fsh/2508%20Commercial%20FSH%20FEM4300%20Series%20Product%20Catalogue.pdf.coredownload.pdf', ['FEM4300']),
      doc('FSH FEM4300 installation instructions (Allegion NZ)', 'https://www.allegion.co.nz/content/dam/allegion-co-nz/resource-hub/downloads/fsh/2508%20Commercial%20FSH%20FEM4300%20Installation%20Instructions.pdf.coredownload.pdf', ['FEM4300']),
    ],
  },
  'fsh-electric-strike': {
    aliases: ['Allegion'],
    warranty: [ALLEGION_COMMERCIAL_WARRANTY],
    docs: [
      doc('FSH FES10 series electric strikes product catalogue (Allegion NZ)', 'https://www.allegion.co.nz/content/dam/allegion-co-nz/resource-hub/downloads/fsh/2108%20Commercial%20FSH%20FES10%20Series%20Electric%20Strikes%20Product%20Catalogue.pdf.coredownload.pdf', ['FES10']),
    ],
  },

  // ── Sylvan (G D Rutter, Glenfield) ──
  'sylvan-sl36-smart-slider': {
    warranty: [doc('Sylvan electronic lock warranty — 2 years mechanical and electronic (G D Rutter)', 'https://sylvan.co.nz/electronic-warranty/', ['Sylvan'])],
    docs: [
      doc('Sylvan SL36E sliding door lock drilling instructions', 'https://sylvan.co.nz/content/installation/SYLSL36SLIDE002_Driling_Instructions.pdf', ['SL36'], true),
      doc('Sylvan SL36E fitting instructions', 'https://sylvan.co.nz/content/installation/SYL_SL36E_Instructions_V9.pdf', ['SL36']),
      doc('Sylvan smart lock programming guide', 'https://sylvan.co.nz/content/installation/SYL_SmartLock_Programming_A4_2025.pdf', ['Sylvan', 'SL36']),
    ],
  },

  // ── Carbine (Davcor) ──
  'carbine-nk2000sd-sliding': {
    aliases: ['Davcor', 'NK2000'],
    warranty: [doc('Carbine warranty against defects (Davcor Group)', 'https://davcor.com.au/warranty/', ['Davcor', 'Carbine'])],
    docs: [
      doc('Carbine NK2000 code change instructions', 'https://carbine.com.au/digital-locks/download/24/product-documentation-files/10669/nk2000-digital-lockset-code-change-instructions-2.pdf', ['NK2000', 'nk2000'], true),
      doc('Carbine NK2000 cut-out template', 'https://carbine.com.au/digital-locks/download/24/product-documentation-files/10667/nk2000-digital-lockset-cutout-template-2.pdf', ['NK2000', 'nk2000'], true),
    ],
  },

  // ── Aqara (Konec Distribution NZ; manuals from Aqara's own support site) ──
  'aqara-u100': {
    warranty: [AQARA_WARRANTY, AQARA_CGA],
    docs: [
      doc('Aqara Smart Lock U100 user manual', 'https://cdn.shopify.com/s/files/1/0710/9220/7830/files/Smart-Door-Lock-U100_User-Manual.pdf?v=1723626779', ['U100']),
      doc('Aqara Smart Lock U100 support page', 'https://store-support.aqara.com/products/aqara-smart-lock-u100', ['U100']),
      doc('Aqara Smart Lock U100 specifications', 'https://www.aqara.com/en/product/smart-lock-u100/specs/', ['U100']),
    ],
  },
  'aqara-u50': {
    warranty: [AQARA_WARRANTY, AQARA_CGA],
    docs: [
      doc('Aqara Smart Lock U50 user manual', 'https://cdn.shopify.com/s/files/1/0710/9220/7830/files/U50-user-manual.pdf?v=1723626675', ['U50']),
      doc('Aqara Smart Lock U50 support page', 'https://store-support.aqara.com/products/aqara-smart-lock-u50', ['U50']),
      doc('Aqara Smart Lock U50 product page and specifications', 'https://www.aqara.com/en/product/smart-lock-u50/', ['U50']),
    ],
  },
  'aqara-u400': {
    warranty: [AQARA_WARRANTY, AQARA_CGA],
    docs: [
      doc('Aqara Smart Lock U400 user manual', 'https://cdn.shopify.com/s/files/1/0710/9220/7830/files/U400UserManual270722.pdf?v=1784866579', ['U400']),
      doc('Aqara Smart Lock U400 support page', 'https://store-support.aqara.com/products/smart-lock-u400', ['U400']),
      doc('Aqara Smart Lock U400 product page and specifications', 'https://www.aqara.com/en/product/smart-lock-u400/', ['U400']),
    ],
  },
  'aqara-u300': {
    warranty: [AQARA_WARRANTY, AQARA_CGA],
    docs: [
      doc('Aqara Smart Lock U300 user manual', 'https://cdn.shopify.com/s/files/1/0710/9220/7830/files/Smart-Lock-U300-User-Manual.pdf?v=1750677409', ['U300']),
      doc('Aqara Smart Lock U300 support page', 'https://store-support.aqara.com/products/aqara-smart-lock-u300', ['U300']),
      doc('Aqara Smart Lock U300 product page and specifications', 'https://www.aqara.com/en/product/smart-lock-u300/', ['U300']),
    ],
  },
  'aqara-u200': {
    warranty: [AQARA_WARRANTY, AQARA_CGA],
    docs: [
      doc('Aqara Smart Lock U200 user manual', 'https://cdn.shopify.com/s/files/1/0710/9220/7830/files/Smart_Lock_U200_User_Manual_EN.pdf?v=1769052798', ['U200']),
      doc('Aqara Smart Lock U200 support page', 'https://store-support.aqara.com/products/aqara-smart-lock-u200', ['U200']),
      doc('Aqara Smart Lock U200 product page and specifications', 'https://www.aqara.com/en/product/smart-lock-u200/', ['U200']),
    ],
  },

  // ── Lockly (NCP Group NZ). Lockly's own warranty is honoured in the USA and Canada only;
  //    here the cover runs through the NZ distributor under the Consumer Guarantees Act. ──
  'lockly-secure-pro': {
    aliases: ['PGK728'],
    warranty: [doc('Lockly limited warranty — 5 years mechanical, 2 years electronics (USA and Canada; NZ cover through the distributor)', 'https://lockly.com/pages/limited-warranty', ['Lockly', 'LOCKLY'])],
    docs: [
      doc('Lockly Secure Pro installation manual', 'https://cdn.shopify.com/s/files/1/0025/4151/8907/files/PGK728WHK-INSTALLATION_MANUAL-240815-WEB.pdf?v=1725540209', ['Secure Pro', 'PGK728']),
      doc('Lockly Secure Pro user manual', 'https://cdn.shopify.com/s/files/1/0025/4151/8907/files/PGK728WHK_User_manual-250121-WEB.pdf?v=1737531142', ['Secure Pro', 'PGK728']),
    ],
  },

  // ── AHS A-Series door operator packages ──
  'ahs-a200-push-and-go': { aliases: ['A-Series', 'A-200SW'], warranty: [AHS_WARRANTY], docs: [AHS_MANUAL, AHS_BROCHURE] },
  'ahs-a200-wireless-activation': { aliases: ['A-Series', 'A-200SW'], warranty: [AHS_WARRANTY], docs: [AHS_MANUAL, AHS_BROCHURE] },
  'ahs-a200-full-install': {
    aliases: ['A-Series', 'A-200SW', 'Trimec', 'Lockwood', 'ES9000', 'ES1111'],
    warranty: [AHS_WARRANTY, TRIMEC_WARRANTY, LOCKWOOD_WARRANTY],
    docs: [AHS_MANUAL, AHS_BROCHURE, doc('ES1111 / ES9000 electric strike installation instructions (ASSA ABLOY NZ)', 'https://eshop.assaabloyopeningsolutions.nz/media/B2BUserGuide/ES9000_Installation_Instructions.pdf', ['ES9000'])],
  },
};

// Hosts a product's documents may live on, per brand: the maker's own sites and its NZ
// distributor's. Anything else is a mirror and fails the test.
const BRAND_HOSTS = {
  'E-LOK': ['www.e-lok.com'],
  Yale: ['www.yalehome.com', 'www.shopyalehome.co.nz'],
  Schlage: ['www.schlage.co.nz', 'images.salsify.com', 'www.allegion.co.nz'],
  Lockwood: ['www.lockweb.com.au', 'www.assaabloy.com', 'eshop.assaabloyopeningsolutions.nz'],
  'Lockwood / FSH': ['www.lockweb.com.au', 'www.assaabloy.com', 'eshop.assaabloyopeningsolutions.nz', 'www.allegion.co.nz'],
  FSH: ['www.allegion.co.nz'],
  Sylvan: ['sylvan.co.nz'],
  Carbine: ['carbine.com.au', 'davcor.com.au'],
  Aqara: ['www.aqarahome.nz', 'store-support.aqara.com', 'www.aqara.com', 'cdn.shopify.com'],
  Lockly: ['lockly.com', 'cdn.shopify.com'],
  AHS: ['ahs.co.nz', 'eshop.assaabloyopeningsolutions.nz', 'www.assaabloy.com'],
};

function docsFor(slug) {
  return productDocs[slug] || null;
}

module.exports = { productDocs, docsFor, BRAND_HOSTS };
